import { useState, useEffect, useMemo } from 'react';
import ColumnFilter from '../columnFilter';
import { hasRoles } from '../../utils';
import CheckBox from '../workingCheckbox';
import { Stack } from '@mui/material';

/**
 * A modal component for managing table views and column configurations.
 *
 * This component provides the ability to:
 * - Save and edit named views that store column visibility and filter states.
 * - Toggle visibility of individual or all columns.
 * - Search through columns by name, filter, or enumeration values.
 * - Apply saved views to update column configurations.
 * - Delete existing views (excluding the "Default" view).
 *
 * ---
 *
 * @param {Object} props - The props passed to the component.
 * @param {Object} props.data - The data object containing current columns and saved views.
 * @param {Array} props.data.columns - An array of column objects, where each column has properties like `header`, `accessor`, `isVisible`, `filter`, and `enumValues`.
 * @param {Array} props.data.views - An array of saved view objects. Each view contains a `name` and an array of `visibleColumns`.
 * @param {Object} props.callbacks - Callback functions for handling various view and column actions.
 * @param {function} props.callbacks.setColumns - A function to update the columns with visibility and filter changes.
 * @param {function} props.callbacks.saveView - A callback to save a new or edited view. Receives the view object and a boolean indicating edit mode.
 * @param {function} props.callbacks.deleteView - A callback to delete a saved view. Receives the name of the view to delete.
 * @param {function} props.callbacks.getColumns - A function to retrieve the current columns.
 * @param {function} props.callbacks.getViews - A function to retrieve the saved views.
 *
 * @returns {JSX.Element} The rendered ViewsModal component with UI elements for managing table views and columns.
 */

const ViewsModal = ({ data, callbacks }) => {
  const [viewName, setViewName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');
  const [isGlobal, setIsGlobal] = useState(false);
  const [columns, setLocalColumns] = useState(data?.columns ?? []);
  const [views, setLocalViews] = useState(data?.views ?? []);

  // Function to handle any column update (visibility or filter)
  const updateColumn = (accessor, update) => {
    callbacks?.setColumns((prevColumns) =>
      prevColumns.map((col) =>
        col.accessor === accessor ? { ...col, ...update } : col
      )
    );
  };

  // Filter columns based on search term
  const filteredColumns = useMemo(() => {
    const search = columnSearch.toLowerCase();

    return columns.filter(({ header = '', filter = '', enumValues = [] }) => {
      const normalizedEnumValues = Array.isArray(enumValues)
        ? enumValues
            .filter((item) => item && typeof item.value !== 'undefined')
            .map(({ value }) =>
              typeof value === 'string'
                ? value.toLowerCase()
                : String(value).toLowerCase()
            )
        : [];

      const searchableValues = [
        header.toLowerCase(),
        filter.toLowerCase(),
        ...normalizedEnumValues,
      ];

      return searchableValues.some((value) => value.includes(search));
    });
  }, [columns, columnSearch]);

  // Save or update the current view
  const handleSaveView = () => {
    if (!viewName.trim()) return alert('View name cannot be empty.');
    if (views.some((view) => view.name === viewName) && !isEditing)
      return alert('A view with this name already exists.');

    callbacks?.saveView(
      { name: viewName, visibleColumns: columns, isGlobal },
      isEditing
    );

    setLocalViews((prevViews) => [
      ...prevViews,
      { name: viewName, visibleColumns: columns, isGlobal },
    ]);
    resetForm();
  };

  // Reset the form after saving or editing
  const resetForm = () => {
    setViewName('');
    setIsEditing(false);
    setIsGlobal(false);
  };

  // Apply a saved view's column configuration
  const handleApplyView = (name) => {
    const view = views.find((v) => v.name === name);
    if (view) {
      callbacks?.setColumns(view.visibleColumns);
      setViewName(name);
      setIsGlobal(view.isGlobal || false);
    }
  };

  // Edit an existing view
  const handleEditView = (view) => {
    if (view.name !== 'Default') {
      setViewName(view.name);
      callbacks?.applyView(view);
      setIsEditing(true);
      setIsGlobal(view.isGlobal || false);
    }
  };

  // Set filter values when columnSearch changes
  useEffect(() => {
    if (callbacks?.setColumns) {
      callbacks.setColumns((prevColumns) => {
        if (!prevColumns) return [];
        return prevColumns.map((col) => ({
          ...col,
          filter: col.filter || '',
        }));
      });
    }
  }, [columnSearch, callbacks?.setColumns]);

  const handleOnCheckboxClick = (accessor, newValue) => {
    updateColumn(accessor, { isVisible: newValue });
  };

  return (
    <div>
      <Stack spacing={2}>
        {/* <h3>Manage Views</h3> */}

        <div>
          <h4>Column Settings</h4>
          <input
            type="text"
            placeholder="Search columns..."
            value={columnSearch}
            onChange={(e) => setColumnSearch(e.target.value)}
          />
          <div>
            <button
              onClick={() =>
                callbacks?.setColumns(
                  columns.map((col) => ({ ...col, isVisible: true }))
                )
              }
            >
              Show All
            </button>
            <button
              onClick={() =>
                callbacks?.setColumns(
                  columns.map((col) => ({ ...col, isVisible: false }))
                )
              }
            >
              Hide All
            </button>
          </div>
        </div>

        <div>
          {filteredColumns.map(
            ({ accessor, header, isVisible, filter, enumValues, type }) => (
              <div
                key={accessor}
                style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
              >
                <CheckBox
                  accessor={accessor}
                  label={header}
                  value={isVisible}
                  callback={handleOnCheckboxClick}
                />

                <ColumnFilter
                  accessor={accessor}
                  filter={filter}
                  enumValues={enumValues}
                  onFilterChange={updateColumn}
                  dataType={type}
                  data={callbacks?.getData()}
                />
              </div>
            )
          )}
        </div>

        <div>
          <h4>{isEditing ? 'Edit View' : 'Save View'}</h4>
          <input
            type="text"
            placeholder="View name"
            value={viewName}
            onChange={(e) => setViewName(e.target.value)}
            disabled={isEditing || viewName === 'Default'}
          />
          <button onClick={handleSaveView} disabled={viewName === 'Default'}>
            {isEditing ? 'Update View' : 'Save View'}
          </button>

          {hasRoles('Owner') && (
            <label>
              <input
                type="checkbox"
                checked={isGlobal}
                onChange={(e) => setIsGlobal(e.target.checked)}
              />
              Save Globally
            </label>
          )}
        </div>

        <div>
          <h4>Available Views</h4>
          <div>
            {views.length > 0 ? (
              views.map(({ name, id, isGlobal }) => (
                <div key={id + (isGlobal ? ' (Global)' : '')}>
                  <label>
                    {name} {isGlobal ? '(Global)' : ''}
                  </label>
                  <button onClick={() => handleApplyView(name)}>Apply</button>
                  {name !== 'Default' &&
                    (!isGlobal || (isGlobal && hasRoles('Owner'))) && (
                      <>
                        <button
                          onClick={() =>
                            handleEditView({
                              name,
                              visibleColumns: columns,
                              isGlobal,
                            })
                          }
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setLocalViews((prevViews) =>
                              prevViews.filter((view) => view.name !== name)
                            );
                            callbacks?.deleteView(name, isGlobal);
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                </div>
              ))
            ) : (
              <p>No views available.</p>
            )}
          </div>
        </div>
      </Stack>
    </div>
  );
};

export default ViewsModal;
